// Sürücü İzinleri — tek sürücü izin düzenleme modalı.
// Haftalık izin günleri (weeklyOffDays) + tek-seferlik izin günleri (optOutDates) yönetilir.
// Geçmiş tarihli izinler korunur (salt-okunur sayaç); sadece bugün ve sonrası düzenlenir.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "shared/components/UIElements/Modal";
import Button from "shared/components/FormElements/Button";

import WeekdayChips from "./WeekdayChips";
import { addDays, expandDateRange, longDate, mergeSortedUnique, todayYMD } from "./availabilityUtils";

const AvailabilityEditModal = ({ show, row, onClose, onSave }) => {
  const { t } = useTranslation();
  const today = todayYMD();

  const [weeklyOffDays, setWeeklyOffDays] = useState([]);
  const [upcomingDates, setUpcomingDates] = useState([]); // bugün ve sonrası optOut günleri
  const [pastDates, setPastDates] = useState([]); // geçmiş optOut (korunur, salt-okunur)
  const [singleDate, setSingleDate] = useState(today);
  const [rangeStart, setRangeStart] = useState(today);
  const [rangeEnd, setRangeEnd] = useState(today);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (show && row) {
      const all = Array.isArray(row.optOutDates) ? row.optOutDates : [];
      setWeeklyOffDays(Array.isArray(row.weeklyOffDays) ? [...row.weeklyOffDays] : []);
      setUpcomingDates(all.filter((d) => d >= today).sort());
      setPastDates(all.filter((d) => d < today).sort());
      setSingleDate(today);
      setRangeStart(today);
      setRangeEnd(today);
      setError(null);
      setSaving(false);
    }
  }, [show, row, today]);

  const toggleWeekday = (day) => {
    setWeeklyOffDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => a - b)));
  };

  const addSingle = () => {
    if (!singleDate) return;
    setUpcomingDates((prev) => mergeSortedUnique(prev, [singleDate]).filter((d) => d >= today));
    setSingleDate(addDays(singleDate, 1));
  };

  const addRange = () => {
    if (!rangeStart || !rangeEnd) return;
    const days = expandDateRange(rangeStart, rangeEnd).filter((d) => d >= today);
    if (days.length === 0) return;
    setUpcomingDates((prev) => mergeSortedUnique(prev, days));
  };

  const removeDate = (date) => {
    setUpcomingDates((prev) => prev.filter((d) => d !== date));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const optOutDates = mergeSortedUnique(pastDates, upcomingDates);
      await onSave(row.driver.id, { weeklyOffDays, optOutDates });
    } catch (err) {
      setError(err?.message || t("driverScheduler.availability.saveFailed"));
      setSaving(false);
    }
  };

  const groupedUpcoming = useMemo(() => groupConsecutive(upcomingDates), [upcomingDates]);

  if (!row) return null;

  return (
    <Modal
      show={show}
      onCancel={onClose}
      onClose={onClose}
      header={t("driverScheduler.availability.editTitle", { name: row.driver.name })}
      className="availability-modal"
      contentClass="availability-modal__content"
      footerClass="availability-modal__footer"
      footer={
        <>
          <Button type="button" inverse onClick={onClose} disabled={saving}>
            {t("driverScheduler.availability.cancel")}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? t("driverScheduler.availability.saving") : t("driverScheduler.availability.save")}
          </Button>
        </>
      }
    >
      {/* Haftalık izin günleri */}
      <section className="availability-modal__section">
        <h4 className="availability-modal__section-title">{t("driverScheduler.availability.weeklyOffTitle")}</h4>
        <p className="availability-modal__hint">{t("driverScheduler.availability.weeklyOffHint")}</p>
        <WeekdayChips selected={weeklyOffDays} onToggle={toggleWeekday} t={t} />
      </section>

      {/* Tek-seferlik izin günleri */}
      <section className="availability-modal__section">
        <h4 className="availability-modal__section-title">{t("driverScheduler.availability.specialOffTitle")}</h4>
        <p className="availability-modal__hint">{t("driverScheduler.availability.specialOffHint")}</p>

        <div className="availability-modal__adders">
          <div className="availability-modal__adder">
            <label className="availability-modal__field-label">{t("driverScheduler.availability.addSingle")}</label>
            <div className="availability-modal__adder-row">
              <input
                type="date"
                className="availability-modal__date-input"
                min={today}
                value={singleDate}
                onChange={(e) => setSingleDate(e.target.value || today)}
              />
              <Button type="button" size="small" onClick={addSingle} disabled={!singleDate}>
                {t("driverScheduler.availability.add")}
              </Button>
            </div>
          </div>

          <div className="availability-modal__adder">
            <label className="availability-modal__field-label">{t("driverScheduler.availability.addRange")}</label>
            <div className="availability-modal__adder-row">
              <input
                type="date"
                className="availability-modal__date-input"
                min={today}
                value={rangeStart}
                onChange={(e) => {
                  const v = e.target.value || today;
                  setRangeStart(v);
                  if (rangeEnd < v) setRangeEnd(v);
                }}
              />
              <span className="availability-modal__range-sep">–</span>
              <input
                type="date"
                className="availability-modal__date-input"
                min={rangeStart}
                value={rangeEnd}
                onChange={(e) => setRangeEnd(e.target.value || rangeStart)}
              />
              <Button type="button" size="small" onClick={addRange} disabled={!rangeStart || !rangeEnd}>
                {t("driverScheduler.availability.add")}
              </Button>
            </div>
          </div>
        </div>

        {/* Yaklaşan izinler */}
        <div className="availability-modal__dates">
          {upcomingDates.length === 0 ? (
            <span className="availability-modal__empty">{t("driverScheduler.availability.noUpcomingLeaves")}</span>
          ) : (
            groupedUpcoming.map((group) => (
              <span key={group.start} className="availability-date-chip">
                <span className="availability-date-chip__label">
                  {group.start === group.end
                    ? longDate(group.start)
                    : `${longDate(group.start)} – ${longDate(group.end)}`}
                  {group.start !== group.end && (
                    <span className="availability-date-chip__count">
                      {" "}
                      ({t("driverScheduler.availability.dayCount", { count: group.count })})
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  className="availability-date-chip__remove"
                  aria-label={t("driverScheduler.availability.remove")}
                  onClick={() => group.dates.forEach(removeDate)}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>

        {pastDates.length > 0 && (
          <p className="availability-modal__past-note">
            {t("driverScheduler.availability.pastLeavesKept", { count: pastDates.length })}
          </p>
        )}
      </section>

      {error && <div className="availability-modal__error">{error}</div>}
    </Modal>
  );
};

/** Ardışık tarihleri tek bir aralık rozetinde gruplar. */
function groupConsecutive(dates) {
  const sorted = [...dates].sort();
  const groups = [];
  for (const d of sorted) {
    const last = groups[groups.length - 1];
    if (last && addDays(last.end, 1) === d) {
      last.end = d;
      last.count += 1;
      last.dates.push(d);
    } else {
      groups.push({ start: d, end: d, count: 1, dates: [d] });
    }
  }
  return groups;
}

export default AvailabilityEditModal;
